/**
 * Best-effort device fingerprint from the User-Agent + Client Hints.
 *
 * Browsers do NOT expose the OS-level hostname ("Konstantinos-iPhone") — that
 * is a hard privacy boundary. We combine UA parsing + `navigator.userAgentData`
 * (Chromium's Client Hints) to produce a human-readable family like
 * `iPhone · Safari 17` or `Mac · Chrome 120`.
 *
 * Pair this with the user-editable `device_label` stored per `device_id` cookie
 * to reach the "Konstantinos-iPhone" style label the operator actually wants.
 */

const UA_OS_RULES = [
  { re: /iPhone/i,               family: "iPhone" },
  { re: /iPad/i,                 family: "iPad" },
  { re: /iPod/i,                 family: "iPod" },
  { re: /Android/i,              family: "Android" },
  { re: /Windows Phone/i,        family: "Windows Phone" },
  { re: /Windows NT/i,           family: "Windows PC" },
  { re: /Macintosh|Mac OS X/i,   family: "Mac" },
  { re: /CrOS/i,                 family: "Chromebook" },
  { re: /Linux/i,                family: "Linux" },
];

const UA_BROWSER_RULES = [
  // Order matters — check the most-specific first.
  { re: /Edg\/(\d+)/,                    name: "Edge" },
  { re: /OPR\/(\d+)/,                    name: "Opera" },
  { re: /SamsungBrowser\/(\d+)/,         name: "Samsung Internet" },
  { re: /Chrome\/(\d+)/,                 name: "Chrome" },
  { re: /Firefox\/(\d+)/,                name: "Firefox" },
  { re: /Version\/(\d+).*Safari/,        name: "Safari" },
];

export function parseUserAgent(ua) {
  const s = ua || (typeof navigator !== "undefined" ? navigator.userAgent : "") || "";
  const os = UA_OS_RULES.find((r) => r.re.test(s));
  const b = UA_BROWSER_RULES.map((r) => {
    const m = s.match(r.re);
    return m ? { name: r.name, version: m[1] } : null;
  }).find(Boolean);
  const family = os?.family || "Unknown device";
  const browser = b ? `${b.name} ${b.version}` : "";
  return { family, browser, label: browser ? `${family} · ${browser}` : family };
}

/**
 * Prefer Chromium's Client Hints when available (more accurate + not spoofable
 * by user agent strings). Falls back to UA parsing.
 */
export async function describeDevice() {
  try {
    const uad = navigator.userAgentData;
    if (uad) {
      const platform = uad.platform || "";
      // Match platform → our family names
      const familyMap = {
        "iOS": /iPhone|iPad/.test(navigator.userAgent) ? (/iPad/.test(navigator.userAgent) ? "iPad" : "iPhone") : "iOS",
        "macOS": "Mac",
        "Windows": "Windows PC",
        "Android": uad.mobile ? "Android phone" : "Android tablet",
        "Chrome OS": "Chromebook",
        "Linux": "Linux",
      };
      const family = familyMap[platform] || platform || parseUserAgent().family;
      const primary = uad.brands?.find((b) => !/not.?a.?brand/i.test(b.brand));
      const browser = primary ? `${primary.brand} ${primary.version}` : "";
      return { family, browser, label: browser ? `${family} · ${browser}` : family };
    }
  } catch { /* fall through */ }
  return parseUserAgent();
}
